import { HttpClient } from '@angular/common/http';

import { inject, Injectable } from '@angular/core';

import {
  AuthService,
  persistVaultAuthMeTenants,
  persistVaultTenantScopeToStorage,
} from '@nebula/shared-vault/data-access';

import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';

import { ServerContextService } from '../../../../feature-vault/src/lib/servers/server-context.service';

import { ProfileService } from '../profile/profile.service';

import {

  firstNonEmptyTenantId,

  persistActiveTenantId,

  resolveVaultApiTenantId,

  syncServerContextFromLocalStorage,

  tenantIdFromAdminTenantsList,
  tenantIdFromAssignUsers,
  tenantIdFromAuthMeResponse,
  tenantIdFromMyTenantsResponse,
  unwrapVaultApiRecord,
} from './tenant-context.util';



/**

 * Hidrata el tenant activo como el legacy `authStore.fetchTenants()`:

 * una sola vez por sesión Vault, compartido por repositorio, ingesta, retención, etc.

 */

@Injectable({ providedIn: 'root' })

export class VaultTenantScopeService {

  private readonly serverContext = inject(ServerContextService);

  private readonly profileService = inject(ProfileService);

  private readonly auth = inject(AuthService);

  private readonly http = inject(HttpClient);



  private hydrateInFlight: Observable<string> | null = null;



  /** Misma prioridad que Ingest: `vault_server_context` y luego cadena de sesión. */

  apiTenantId(): string {

    this.auth.syncVaultTenantFromAccessToken();

    syncServerContextFromLocalStorage(this.serverContext);

    return resolveVaultApiTenantId(this.serverContext);

  }



  /** Garantiza tenant en contexto (my-tenants → me → admin tenants → JWT). Idempotente. */
  ensureHydrated$(): Observable<string> {
    this.auth.syncVaultTenantFromAccessToken();
    syncServerContextFromLocalStorage(this.serverContext);

    const existing = this.apiTenantId();
    if (existing) {
      return of(existing);
    }

    if (this.hydrateInFlight) {
      return this.hydrateInFlight;
    }

    this.hydrateInFlight = this.profileService.getMyTenants().pipe(
      catchError(() => of(null)),
      switchMap((myTenants) => {
        const fromList = tenantIdFromMyTenantsResponse(myTenants);
        if (fromList) {
          this.persistResolvedTenant(fromList, myTenants);
        } else if (myTenants) {
          this.persistTenantNamesFromApi(myTenants);
        }

        return this.profileService.getMe().pipe(
          catchError(() => of(null)),
          switchMap((me) => {
            const fromMe = tenantIdFromAuthMeResponse(me);
            const resolved = fromMe || fromList;
            if (resolved) {
              this.persistResolvedTenant(resolved, me ?? myTenants);
              return of(resolved);
            }

            if (!this.auth.isVaultSuperadmin()) {
              return of(resolveVaultApiTenantId(this.serverContext));
            }

            return this.http.get<unknown>('/api/admin/tenants').pipe(
              catchError(() => of(null)),
              map((adminTenants) => {
                const fromAdmin = tenantIdFromAdminTenantsList(adminTenants, {
                  pickFirst: true,
                });
                if (fromAdmin) {
                  this.persistResolvedTenant(fromAdmin);
                }
                return fromAdmin || resolveVaultApiTenantId(this.serverContext);
              }),
            );
          }),
        );
      }),
      tap((tenantId) => {
        this.hydrateInFlight = null;
        if (tenantId) {
          this.notifySessionUpdated();
        }
      }),
      catchError(() => {
        this.hydrateInFlight = null;
        return of(this.apiTenantId());
      }),
    );

    return this.hydrateInFlight;
  }

  private persistResolvedTenant(tenantId: string, tenantsRaw?: unknown): void {
    const id = (tenantId || '').trim();
    if (!id) return;
    persistActiveTenantId(id, this.serverContext);
    persistVaultTenantScopeToStorage(id);
    if (tenantsRaw != null) {
      this.persistTenantNamesFromApi(tenantsRaw);
    }
  }

  private persistTenantNamesFromApi(raw: unknown): void {
    if (Array.isArray(raw)) {
      persistVaultAuthMeTenants(raw);
      return;
    }
    const record = unwrapVaultApiRecord(raw);
    if (record && Array.isArray(record['tenants'])) {
      persistVaultAuthMeTenants(record['tenants']);
    }
  }

  private notifySessionUpdated(): void {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('vault-session-updated'));
    }
  }



  /** Resuelve tenant para asignar (selector, usuarios, señales UI, API). */

  resolveForAssign$(options: {

    repositoryTenantId?: string;

    assignScopeTenantId?: string;

    authMeTenantId?: string;

    assignUsers?: Array<{ id?: string; tenants?: Array<{ tenant_id?: string }> }>;

    selectedUserIds?: string[];

    isSuperAdmin?: boolean;

  }): Observable<string> {

    const users = options.assignUsers ?? [];

    const selected = options.selectedUserIds ?? [];

    const fromUsers = tenantIdFromAssignUsers(users, selected);

    const immediate = firstNonEmptyTenantId(

      options.isSuperAdmin ? options.repositoryTenantId : '',

      options.assignScopeTenantId,

      fromUsers,

      options.authMeTenantId,

      this.apiTenantId(),

    );

    if (immediate) {

      persistActiveTenantId(immediate, this.serverContext);

      return of(immediate);

    }

    return this.ensureHydrated$().pipe(

      map(

        (hydrated) =>

          firstNonEmptyTenantId(

            hydrated,

            options.isSuperAdmin ? options.repositoryTenantId : '',

            options.assignScopeTenantId,

            fromUsers,

            options.authMeTenantId,

            this.apiTenantId(),

          ) || '',

      ),

      tap((tid) => {

        if (tid) {

          persistActiveTenantId(tid, this.serverContext);

        }

      }),

    );

  }

}

